import React, { useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, gql } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const GET_PUBLIC_VISIT_TRACKING = gql`
  query GetPublicVisitTracking($visitId: ID!, $trackingToken: String!) {
    publicVisitTracking(visitId: $visitId, trackingToken: $trackingToken) {
      id
      purpose
      scheduledAt
      status
      rejectionReason
      visitFeedbackLink
      visitor {
        firstName
        lastName
        email
      }
      host {
        firstName
        lastName
      }
      department {
        name
      }
      feedback {
        id
        rating
        comment
      }
    }
  }
`;

const SUBMIT_PUBLIC_VISIT_FEEDBACK = gql`
  mutation SubmitPublicVisitFeedback(
    $visitId: ID!
    $trackingToken: String!
    $rating: Int!
    $comment: String
  ) {
    submitPublicVisitFeedback(
      visitId: $visitId
      trackingToken: $trackingToken
      rating: $rating
      comment: $comment
    ) {
      success
      message
      feedback {
        id
        rating
        comment
      }
    }
  }
`;

const getStatusLabel = (status) => ({
  PENDING: 'En attente',
  APPROVED: 'Approuvée',
  REJECTED: 'Rejetée',
  COMPLETED: 'Terminée',
  CANCELLED: 'Annulée',
}[status] || status);

const getStatusColor = (status) => ({
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  COMPLETED: 'info',
  CANCELLED: 'default',
}[status] || 'default');

function PublicVisitTracking() {
  const [searchParams] = useSearchParams();
  const visitId = searchParams.get('registerVisit');
  const trackingToken = searchParams.get('token');
  const feedbackMode = searchParams.get('feedback') === '1';
  const [rating, setRating] = useState('');
  const [comment, setComment] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const { data, loading, error, refetch } = useQuery(GET_PUBLIC_VISIT_TRACKING, {
    variables: { visitId, trackingToken },
    skip: !visitId || !trackingToken,
    fetchPolicy: 'network-only',
  });

  const [submitFeedback, { loading: submittingFeedback }] = useMutation(SUBMIT_PUBLIC_VISIT_FEEDBACK, {
    onCompleted: async ({ submitPublicVisitFeedback }) => {
      if (!submitPublicVisitFeedback?.success) {
        setFeedbackMessage(submitPublicVisitFeedback?.message || 'Impossible d’enregistrer votre feedback.');
        return;
      }
      setFeedbackMessage(submitPublicVisitFeedback.message || 'Merci pour votre retour');
      await refetch();
    },
    onError: (mutationError) => {
      setFeedbackMessage(
        mutationError.graphQLErrors?.[0]?.message || mutationError.message || 'Impossible d’enregistrer votre feedback.'
      );
    },
  });

  const visit = data?.publicVisitTracking;
  const hasFeedback = Boolean(visit?.feedback?.id);

  const handleSubmitFeedback = (event) => {
    event.preventDefault();
    setFeedbackMessage('');

    if (!rating) {
      setFeedbackMessage('Veuillez sélectionner une note.');
      return;
    }

    submitFeedback({
      variables: {
        visitId,
        trackingToken,
        rating: Number(rating),
        comment: comment.trim() || null,
      },
    });
  };

  if (!visitId || !trackingToken) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">Le lien de suivi est incomplet.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" minHeight="300px" alignItems="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !visit) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.graphQLErrors?.[0]?.message || error?.message || 'Impossible de charger les informations de la visite.'}
        </Alert>
        <Button component={RouterLink} to="/" variant="outlined">Retour à l'accueil</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
      <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Box>
            <Typography variant="overline" color="primary.main">
              {feedbackMode ? 'Feedback de visite' : 'Suivi de visite'}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {feedbackMode ? 'Partagez votre avis' : 'Etat de votre visite'}
            </Typography>
          </Box>
          <Chip label={getStatusLabel(visit.status)} color={getStatusColor(visit.status)} />
        </Box>

        {visit.status === 'REJECTED' && visit.rejectionReason && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Votre visite a été rejetée. Motif : {visit.rejectionReason}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: feedbackMode ? 4 : 0 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Visiteur</Typography>
            <Typography>{visit.visitor?.firstName} {visit.visitor?.lastName}</Typography>
            <Typography variant="body2" color="text.secondary">{visit.visitor?.email}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Hôte</Typography>
            <Typography>{visit.host?.firstName} {visit.host?.lastName}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Département</Typography>
            <Typography>{visit.department?.name}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Date prévue</Typography>
            <Typography>{format(new Date(visit.scheduledAt), "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Motif</Typography>
            <Typography>{visit.purpose}</Typography>
          </Grid>
        </Grid>

        {feedbackMode && (
          <>
            {visit.status !== 'COMPLETED' ? (
              <Alert severity="info">Le formulaire de feedback est disponible uniquement pour une visite terminée.</Alert>
            ) : hasFeedback ? (
              <Alert severity="success">
                Merci, votre feedback a déjà été enregistré.
                {visit.feedback?.rating ? ` Note : ${visit.feedback.rating}/5.` : ''}
                {visit.feedback?.comment ? ` Commentaire : ${visit.feedback.comment}` : ''}
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleSubmitFeedback}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Votre lien de feedback reste valable 3 jours après la date de la visite.
                </Alert>
                {feedbackMessage && (
                  <Alert severity={feedbackMessage.startsWith('Merci') ? 'success' : 'error'} sx={{ mb: 3 }}>
                    {feedbackMessage}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Note"
                      value={rating}
                      onChange={(event) => setRating(event.target.value)}
                      size="small"
                      required
                    >
                      {[1, 2, 3, 4, 5].map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}/5
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Commentaire"
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      placeholder="Partagez votre expérience"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button type="submit" variant="contained" disabled={submittingFeedback}>
                      {submittingFeedback ? 'Envoi...' : 'Envoyer mon feedback'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </>
        )}

        {!feedbackMode && visit.status === 'COMPLETED' && !hasFeedback && (
          <Box sx={{ mt: 4 }}>
            <Button component="a" href={visit.visitFeedbackLink} variant="contained">
              Donner mon avis
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default PublicVisitTracking;
